/*
 * *****************************************************************************
 *  Copyright (C)  Motorola Solutions, INC.
 *  All Rights Reserved.
 *  Motorola Solutions Confidential Restricted.
 * *****************************************************************************
 */

import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType, OnInitEffects } from '@ngrx/effects';
import { catchError, filter, map, switchMap } from 'rxjs/operators';
import {
    ccHubAttachedEvent,
    ccHubEffectsInitialized,
    ccHubHeadsetConnectedEvent,
    ccHubInitiateUpgrade,
    ccHubUpgradeFailure,
    ccHubUpgradeSuccess, fetchVersionFailure, fetchVersionSuccess,
    muteAction
} from './cchub.actions';
import { CchubService } from '../services/cchub.service';
import { defer, Observable, of } from 'rxjs';
import { Action, Store } from '@ngrx/store';
import { updateMediaDevices } from './media.actions';
import { CcHubAudioService } from '../services/cc-hub-audio.service';
import { WebUsbSwdlService } from '../services/web-usb-swdl.service';
import { hotKeyTriggered } from '../../configuration/+state/configuration.actions';
import { Headset, Headsets } from '../../configuration/model/headsets';
import { HotKeyAction } from '../../core/model/hotkey';
import { selectCCHubHeadsetConnected } from './cchub.selectors';
import { SkipperService } from '../../core/services/skipper.service';
import { fetchVersions } from '../../settings/+state/settings.actions';
import { EnvironmentService } from '../../core/services/environment.service';

@Injectable()
export class CchubEffects implements OnInitEffects {
    constructor(
        private store: Store,
        private actions$: Actions,
        private ccHubService: CchubService,
        private webUsbSwdlService: WebUsbSwdlService,
        private ccHubAudioService: CcHubAudioService,
        private skipperService: SkipperService,
        private env: EnvironmentService,
    ) {
        this.store.select(selectCCHubHeadsetConnected).pipe(
        ).subscribe((isConnected) => this.store.dispatch(ccHubHeadsetConnectedEvent({ isConnected })));
    }

    ngrxOnInitEffects(): Action {
        return ccHubEffectsInitialized();
    }

    //logic derived from web-usb-helper.js amICchub method.
    ccHubAttached$ = createEffect(() =>
        this.actions$.pipe(
            ofType(updateMediaDevices),
            map(({ devices }) => ccHubAttachedEvent({ isAttached: devices.findIndex((device) =>
                device.label.includes(CcHubAudioService.CC_HUB_INPUT_HEADSET_DEVICE_LABEL) ||
                device.label.includes(CcHubAudioService.CC_HUB_OUTPUT_HEADSET_DEVICE_LABEL)) >= 0 }))
        )
    );

    ccHubUpgrading$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ccHubInitiateUpgrade),
            switchMap(() =>
                this.webUsbSwdlService.upgrade().pipe(
                    map(() => ccHubUpgradeSuccess()),
                    catchError((err: Error) => of(ccHubUpgradeFailure({ payload: err.message })))
                )
            )
        )
    );

    muteCCHubDeviceHotkeyHandler$: Observable<{}> = createEffect(() =>
        this.actions$.pipe(
            ofType(hotKeyTriggered),
            filter(({ hotkey }) => hotkey.action === HotKeyAction.MUTE && hotkey.params[0] !== Headset.SYSTEM),
            map(({ hotkey }) => muteAction( { peripheral: Headsets.deviceMap[hotkey.params[0] as Headset] } ))
        )
    );

    fetchVersionInformation$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fetchVersions),
            switchMap(() =>
                defer(() => this.skipperService.fetchVersionInformation()).pipe(
                    map((version) => fetchVersionSuccess({ version })),
                    catchError(() =>
                        defer(() => of(fetchVersionSuccess({ version: this.env.environment.BUILD_NUMBER }))).pipe(
                            catchError((err: Error) => of(fetchVersionFailure({ payload: err.message })))
                        )
                    )
                )
            )
        )
    );
}
